import { IconAddPlus } from '@components/icons/add-plus';
import { Spinner } from '@components/loading-spinner';
import { Sidebar, showSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import {
  useCreateJourneyAction,
  useInvalidateJourneyActions,
  useJourneyActionIsInsideLoop,
} from 'api/journey-actions/hooks';
import { JourneyType } from 'api/types';

import { ActionTypeSelector, OnCreateActionSelectProps } from './action-type-selector';

import { showActionSidebar } from '../action-sidebar';
import { EdgeProps } from '../base-builder';
import { hiddenIdentifier } from '../edge';

type CreateActionSidebarProps = {
  journeyId: JourneyType['shortId'];
  edge: Pick<EdgeProps, 'id' | 'source' | 'target'>;
};

export const showCreateActionSidebar = (props: CreateActionSidebarProps) =>
  showSidebar({
    component: CreateActionSidebar,
    options: { variant: 'REGULAR', id: props.edge.id },
    props,
  });

const CreateActionSidebar = ({ edge, journeyId }: CreateActionSidebarProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const createMutation = useCreateJourneyAction();
  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const isLeaf = edge.target.endsWith(hiddenIdentifier);
  const targetNodeMutation = useJourneyActionIsInsideLoop(edge.target, { enabled: !isLeaf });
  const isLoading = !isLeaf && targetNodeMutation.isLoading;

  const createAction = async ({ actionType, values }: OnCreateActionSelectProps) => {
    const newAction = await createMutation.mutateAsync({
      journey: journeyId,
      parentAction: edge.source,
      childActions: edge.target === 'hidden' ? [] : [edge.target],
      isAutomatic: true,
      action: actionType,
      ...(values && { values }),
    });
    await invalidateJourneyActions();
    showActionSidebar({ id: newAction.shortId, type: actionType }, { isNewAction: true });
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('title')} icon={IconAddPlus} iconColor="elementBlue100" />
      {isLoading ? (
        <Spinner />
      ) : (
        <Sidebar.Content>
          <Stack space={4}>
            <Stack space={2}>
              <div>
                <Text text={t('chooseAction')} font="bold16" />
              </div>
              <Text text={t('selectStep')} font="regular14" color="textSecondary" />
            </Stack>
            <ActionTypeSelector
              onSelect={createAction}
              isInsideLoop={!isLeaf && targetNodeMutation.data}
              isDisabled={createMutation.isLoading}
            />
          </Stack>
        </Sidebar.Content>
      )}
    </Sidebar>
  );
};

const sidebarLocale = {
  en: {
    title: 'Add action',
    chooseAction: 'Choose action',
    selectStep: 'Select what this step in your workflow does',
  },
};
